import { Nullable } from '@jamesgmarks/utilities';

import { dispatch } from '../../store';
import {
  contractsReceived,
  setContractsLoadedState,
  timelineReceived,
  setLoadedState,
} from './promotedListingsSlice';
import { ELoadState, IApiQueryListResponse, apiFetch, typedApiFetch } from '../../utils';
import { REACT_APP_API_ROOT_URI } from '../../../App';
import {
  IPromotedListingContract,
  IPromotedListingContractPriceUpdate,
  IPromotedListingContractPriceUpdateResult,
} from '../../../entity-interfaces/IPromotedListingContractPricing';
import { IPromotedListingSegmentTimeline } from './interfaces';
import { assertYearMonth, IYearMonth } from '../../../interfaces/IYearMonth';
import { showMessage } from '../messaging/actions';

export const loadTimeline = async (freshbooksClientId: number, yearMonth:IYearMonth) => {
  const {year, month} = assertYearMonth(yearMonth) && yearMonth;
  const url = `${REACT_APP_API_ROOT_URI}/promoted_listings/timeline/${year}-${month}/${freshbooksClientId}`;
  dispatch(setLoadedState('loading'));
  const response = await typedApiFetch<IApiQueryListResponse<IPromotedListingSegmentTimeline>>(url);
  const { data } = await response.json();
  dispatch(timelineReceived(data)); // TODO: Error handling?
  dispatch(setLoadedState('loaded'));
};

/**
 * Calls a promoted listing contract pricing endpoint and unwraps its `data` payload.
 *
 * These endpoints answer a rejected change with a 400 and a human-readable `error` string — an
 * unknown subscription, one that is not a promoted listing contract, or a bracket set that cannot
 * be applied — so that message is surfaced rather than the generic status warning `apiFetch`
 * already raises.
 * @returns The response payload, or `null` if the request failed.
 */
const requestContractPricing = async <TData>(
  path: string,
  init: RequestInit = {},
): Promise<Nullable<TData>> => {
  /*
   * Built inside the function, not hoisted to a module constant: `App.tsx` imports the route tree
   * that reaches this file, so reading `REACT_APP_API_ROOT_URI` while this module is evaluating
   * hits its temporal dead zone.
   */
  const response = await apiFetch(`${REACT_APP_API_ROOT_URI}/promoted_listings${path}`, init);

  if (!response.ok) {
    const errorBody: Nullable<{ error?: string | string[] }> = await response.json().catch(() => null);
    const errorMessage = errorBody?.error;

    showMessage({
      message: (
        typeof errorMessage === 'string'
          ? errorMessage
          : `Promoted listing contract pricing request failed: ${path}`
      ),
      severity: 'error',
      asAlert: true,
    });

    return null;
  }

  const { data }: { data: TData } = await response.json();

  return data;
};

/** Loads the promoted listing contract subscriptions, and their price brackets, for one client. */
export const loadPromotedListingContracts = async (clientId: number) => {
  dispatch(setContractsLoadedState(ELoadState.loading));

  const contracts = await requestContractPricing<IPromotedListingContract[]>(`/contracts/${clientId}`);

  dispatch(contractsReceived(contracts ?? []));
  dispatch(setContractsLoadedState(contracts ? ELoadState.loaded : ELoadState.error));
};

/** Clears the loaded contracts, so switching clients cannot leave the previous one's on screen. */
export const clearPromotedListingContracts = () => {
  dispatch(contractsReceived([]));
  dispatch(setContractsLoadedState(ELoadState.idle));
};

/**
 * Previews or applies a price bracket change to one promoted listing contract subscription.
 *
 * A committed save reloads the client's contracts so the editor reflects what was written rather
 * than what was submitted; a dry run leaves the store untouched.
 *
 * @param subscriptionId The contract subscription being repriced.
 * @param update The bracket set to apply, whether to fall back to plan prices, and the dry-run flag.
 * @returns The before/after brackets and affected listing subscriptions, or `null` if rejected.
 */
export const savePromotedListingContractPrices = async (
  subscriptionId: number,
  update: IPromotedListingContractPriceUpdate,
): Promise<Nullable<IPromotedListingContractPriceUpdateResult>> => {
  const result = await requestContractPricing<IPromotedListingContractPriceUpdateResult>(
    `/contracts/${subscriptionId}/prices`,
    { method: 'PUT', body: JSON.stringify(update) },
  );

  if (result && !result.dryRun) {
    await loadPromotedListingContracts(result.clientId);
  }

  return result;
};
